// src/components/About.tsx
import { Card, CardContent } from '@/components/ui/card';
import { Brush, Code, Lightbulb, Palette } from 'lucide-react';
import AnimatedSection from './AnimatedSection';

const About = () => {
  const skills = [
    {
      icon: <Brush className="h-8 w-8 text-primary" />,
      title: 'Visual Art',
      description: 'Creating captivating visual stories through various mediums including painting, drawing, and digital art.'
    },
    {
      icon: <Palette className="h-8 w-8 text-primary" />,
      title: 'Design',
      description: 'Crafting beautiful, functional designs that effectively communicate ideas and enhance user experiences.'
    },
    {
      icon: <Code className="h-8 w-8 text-primary" />,
      title: 'Creative Direction',
      description: 'Guiding creative projects from concept to completion with a cohesive vision and strategic approach.'
    },
    {
      icon: <Lightbulb className="h-8 w-8 text-primary" />,
      title: 'Illustration',
      description: 'Bringing concepts to life through detailed and expressive illustrated visuals tailored to project needs.'
    }
  ];

  return (
    <section id="about" className="py-24 px-4 overflow-hidden">
      <div className="container mx-auto max-w-5xl">
        <AnimatedSection
          className="text-center mb-16"
          variant="fade-up"
        >
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">About Me</h2>
          <h3 className="text-3xl md:text-4xl font-bold mb-6">Who I Am</h3>
          <div className="h-1 w-20 bg-primary mx-auto"></div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <AnimatedSection
            variant="fade-right"
            className="space-y-6"
          >
            <h4 className="text-2xl font-display font-medium">
              I'm a visual artist with a passion for creating meaningful experiences
            </h4>
            <p className="leading-relaxed">
              With over a decade of experience, I specialize in visual arts, design, and creative direction. My work spans multiple disciplines, blending traditional artistic techniques with modern digital approaches.
            </p>
            <p className="leading-relaxed">
              I believe that art and design have the power to transform perspectives and create meaningful connections. My approach is characterized by attention to detail, thoughtful composition, and a deep understanding of visual storytelling.
            </p>
            <p className="leading-relaxed">
              When I'm not creating, I enjoy exploring new artistic techniques, visiting galleries, and finding inspiration in the world around me.
            </p>
          </AnimatedSection>

          <AnimatedSection
            variant="fade-left"
            delay={200}
            className="relative"
          >
            <div className="glass-card rounded-2xl overflow-hidden aspect-[4/5] relative">
              <img
                src="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91"
                alt="Priya Pareek"
                className="object-cover w-full h-full"
                loading="lazy"
              />
            </div>
            <div 
              className="absolute -z-10 inset-0 bg-[radial-gradient(circle_at_center,rgba(120,119,198,0.15),transparent_70%)]"
              style={{ transform: 'translate(20%, 20%)' }}
              aria-hidden="true"
            />
          </AnimatedSection>
        </div>

        <div className="mt-24">
          <AnimatedSection
            className="text-center mb-16"
            variant="fade-up"
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-6">My Skills</h3>
            <div className="h-1 w-20 bg-primary mx-auto"></div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.map((skill, index) => (
              <AnimatedSection
                key={index}
                variant="fade-up"
                delay={index * 100}
              >
                <Card className="glass-card h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="mb-4 p-3 rounded-full bg-secondary">
                      {skill.icon}
                    </div>
                    <h4 className="text-xl font-medium mb-2">{skill.title}</h4>
                    <p className="text-muted-foreground">{skill.description}</p>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;